/**
 * @lines98/client/lib/skin-registry — ДЕКЛАРАТИВНЫЙ реестр ОФОРМЛЕНИЯ доски: ДВЕ
 * ортогональные оси (appearance-skins.md §4). Owner-directive 2026-06-04 «подраздел
 * со скинами — модуль, туда постоянно добавляем новые темы»; расширено до двух осей
 * owner-decision 2026-06-15/16 (appearance-skins.md).
 *
 * ЗАЧЕМ ДВЕ ОСИ / WHY THIS EXISTS:
 *   - Ось «Доска» (палитра, `BOARD_PALETTES`) — карта CSS-переменных (цвета фона
 *     доски / клеток / 7 шаров). Накладывается на корень доски через `lib/board-
 *     appearance`. Дефолт `standard` — пустая карта (берёт токены global.css).
 *   - Ось «Шары» (материал, `BALL_STYLES`) — рецепт блика шара (`data-ball-style` →
 *     `board.css`), читающий уже-установленный ЦВЕТ шара. Визуальный рецепт живёт в
 *     CSS (board.css по `data-ball-style=id`), НЕ в данных.
 *   Оси ортогональны (I-App-2): палитра задаёт ЦВЕТА, материал — КАК блик. N·M видов
 *   без ручной отрисовки комбинаций (палитра меняет `--ball-N`, материал читает их
 *   через `--bc` + производные `--lit/--glow` color-mix'ом — одна формула на все
 *   цвета И все палитры, board.css).
 *
 * Добавить палитру/материал = ОДНА запись в соответствующий массив (как
 * `lib/mode-registry` для режимов): и галерея (`lib/swatch-gallery` + под-экран
 * `components/skin-gallery`), и применение (`lib/board-appearance`) читают отсюда,
 * ничего не дублируя.
 *
 * PREMIUM-флаг (future-paid, §2.3): не-дефолтный элемент несёт `premium:true` + слот
 * `badge`. СЕЙЧАС всё выбираемо без блокировки и БЕЗ видимого бейджа (owner 2026-06-16:
 * видимый бейдж «Скоро платно» добавим перед релизом) — `badge` пуст → слот скрыт.
 *
 * NB: оформление доски НЕЗАВИСИМО от темы приложения (light/dark) — CLAUDE.md I-1
 * (продаваемый косметический продукт, no-p2w). Палитра задаёт цвета доски/шаров, не
 * фон интерфейса.
 *
 * **ЛОКАЛИЗАЦИЯ ИМЁН (i18n, ADR-0021):** видимые `name` палитр/материалов —
 * GETTER'ы, резолвящие `t('game.appearance.palette|material.<id>')` в МОМЕНТ чтения
 * (реактивность локали). Литералов имён в реестре нет; галерея (`components/skin-
 * gallery`) пере-читает `def.name` на смену языка через свой `relabel()`.
 *
 * Cross-references: appearance-skins.md §3/§4; `lib/board-appearance` (применение);
 * `lib/swatch-gallery` (сетка свотчей); `content/i18n/<lang>.json`
 * `game.appearance.palette|material.<id>` (имена — 15 языков); `lib/i18n` `t()`;
 * `styles/board.css` (рецепты материалов); `styles/global.css` :root (дефолт-токены).
 */

import { t } from './i18n/index.js';

// =============================================================================
// Ось «Доска» — палитры (цвета через CSS-переменные)
// =============================================================================

export interface PaletteDef {
  /** Стабильный id (persist в `client-prefs.boardSkin`; id НЕ менялись — без миграции). */
  readonly id: string;
  /**
   * Видимое имя (must-visible подпись свотча). GETTER: резолвит
   * `t('game.appearance.palette.<id>')` в момент чтения (локаль-реактивность).
   */
  readonly name: string;
  /**
   * Не-дефолтный элемент → future-paid. СЕЙЧАС всё равно выбираем без гейта (§2.3,
   * AC-5). Флаг — задел под монетизацию (заполнить `badge` + гейт перед релизом).
   */
  readonly premium?: boolean;
  /** Слот бейджа; сейчас пусто/undefined → бейдж скрыт (AC-5). */
  readonly badge?: string;
  /**
   * Палитра-оверрайд (CSS-переменная → значение), накладывается на корень доски
   * (`lib/board-appearance`). Пустая/undefined у `standard` (берёт токены global.css).
   * Переменные — из global.css :root (`--board-bg*`, `--cell`, `--ball-1..7`).
   */
  readonly palette?: Readonly<Record<string, string>>;
}

/**
 * @deprecated Историческое имя — оставлено как алиас, чтобы не ломать внешние
 * импорты `SkinDef`/`getSkin` одним PR. Новый код использует `PaletteDef`.
 */
export type SkinDef = PaletteDef;

/**
 * Id дефолтной палитры (на неё ссылается дефолт `client-prefs.boardSkin`) — единый
 * источник, чтобы не дублировать литерал строки по коду.
 */
export const STANDARD_PALETTE_ID = 'standard';
/** @deprecated алиас `STANDARD_PALETTE_ID` (back-compat внешних импортов). */
export const STANDARD_SKIN_ID = STANDARD_PALETTE_ID;

/**
 * Реестр палитр доски. `standard` — дефолт (пустая карта). Остальные — реально
 * применяемые палитры (не заглушки «Скоро»): `premium`-флаг задел под монетизацию,
 * но сейчас все выбираемы. Расширяемо: добавь запись — галерея/применение подхватят.
 */
export const BOARD_PALETTES: readonly PaletteDef[] = [
  {
    id: STANDARD_PALETTE_ID,
    get name() {
      return t(`game.appearance.palette.${STANDARD_PALETTE_ID}`);
    },
  },
  {
    id: 'emerald',
    get name() {
      return t('game.appearance.palette.emerald');
    },
    premium: true,
    // Ось «Доска» меняет ТОЛЬКО фон/клетки, НИКОГДА не цвет шаров (owner-directive
    // 2026-06-16). Цвета шаров — фиксированные --ball-1..7 (global.css); материал —
    // ось «Шары».
    palette: {
      '--board-bg': '#1f3b2e',
      '--board-bg-2': '#163024',
      '--cell': '#bfe3cd',
    },
  },
  {
    id: 'sapphire',
    get name() {
      return t('game.appearance.palette.sapphire');
    },
    premium: true,
    palette: {
      '--board-bg': '#1b2a4a',
      '--board-bg-2': '#142b4a',
      '--cell': '#c2d2f0',
    },
  },
  {
    id: 'sunset',
    get name() {
      return t('game.appearance.palette.sunset');
    },
    premium: true,
    palette: {
      '--board-bg': '#4a2230',
      '--board-bg-2': '#3a1a26',
      '--cell': '#f0d2c8',
    },
  },
];

/**
 * @deprecated алиас `BOARD_PALETTES` (back-compat внешних импортов `SKINS`).
 */
export const SKINS: readonly PaletteDef[] = BOARD_PALETTES;

/** Найти палитру по id (fallback — standard; невалидный сохранённый id не крашит). */
export function getPalette(id: string): PaletteDef {
  return BOARD_PALETTES.find((p) => p.id === id) ?? BOARD_PALETTES[0]!;
}
/** @deprecated алиас `getPalette` (back-compat внешних импортов `getSkin`). */
export const getSkin = getPalette;

// =============================================================================
// Ось «Шары» — материалы (рецепт блика через data-атрибут)
// =============================================================================

export interface BallStyleDef {
  /**
   * Стабильный id (persist в `client-prefs.ballStyle`). Совпадает со значением
   * `data-ball-style` на корне доски → board.css по нему выбирает рецепт.
   */
  readonly id: string;
  /**
   * Видимое имя (must-visible подпись свотча). GETTER: резолвит
   * `t('game.appearance.material.<id>')` в момент чтения (локаль-реактивность).
   */
  readonly name: string;
  /** Не-дефолтный → future-paid (§2.3). Сейчас выбираем без гейта (AC-5). */
  readonly premium?: boolean;
  /** Слот бейджа; сейчас пусто → скрыт (AC-5). */
  readonly badge?: string;
  // Визуальный рецепт (background-image/box-shadow) живёт в board.css по
  // data-ball-style=id (НЕ в данных) — материал не задаёт цвета, только блик.
}

/** Id дефолтного материала (на него ссылается дефолт `client-prefs.ballStyle`). */
export const CLASSIC_BALL_STYLE_ID = 'classic';

/**
 * Реестр материалов шара. `classic` — дефолт (текущий рецепт `.ball`). `glass` /
 * `pearl` — рецепты из прототипов `docs/art/ball-style-*.html` (перенесены в
 * board.css). Расширяемо: новый материал = запись здесь + рецепт в board.css.
 */
export const BALL_STYLES: readonly BallStyleDef[] = [
  {
    id: CLASSIC_BALL_STYLE_ID,
    get name() {
      return t(`game.appearance.material.${CLASSIC_BALL_STYLE_ID}`);
    },
  },
  {
    id: 'glass',
    get name() {
      return t('game.appearance.material.glass');
    },
    premium: true,
  },
  {
    id: 'pearl',
    get name() {
      return t('game.appearance.material.pearl');
    },
    premium: true,
  },
];

/** Найти материал по id (fallback — classic; невалидный сохранённый id не крашит). */
export function getBallStyle(id: string): BallStyleDef {
  return BALL_STYLES.find((b) => b.id === id) ?? BALL_STYLES[0]!;
}
